import { getQueryTimeseriesForNodeUseCase } from "@src/cloud-connect/use-cases/get-query-timeseries-for-node";
import type {
  DrBoyMachineDataResponse,
  DrBoyMachineState,
} from "@src/types/factory-dashboard-dr-boy";

interface SensorValue {
  value: number | string | null;
  time: string;
}

type DrBoyMachineOverviewField =
  | "machineMode"
  | "job"
  | "nominalParts"
  | "jobGoodPartsCounter"
  | "cycleTime"
  | "jobBadPartsCounter";

type DrBoyMachineNodeMapping = Record<DrBoyMachineOverviewField, string>;

interface MachineSensorEntry {
  meta_data: Record<string, unknown>;
  values: { time: string; value: number | string | null }[];
  timeseries_type: string;
}

const DR_BOY_MACHINE_OVERVIEW_FIELDS: readonly DrBoyMachineOverviewField[] = [
  "machineMode",
  "job",
  "nominalParts",
  "jobGoodPartsCounter",
  "cycleTime",
  "jobBadPartsCounter",
];

function normalizeSensorEntry(entry: MachineSensorEntry): SensorValue {
  const latest = entry.values[0];
  return {
    value: latest?.value ?? null,
    time: latest?.time ?? "",
  };
}

const createInitialMachineState = (): DrBoyMachineState =>
  DR_BOY_MACHINE_OVERVIEW_FIELDS.reduce((acc, field) => {
    acc[field] = { value: null, time: "" };
    return acc;
  }, {} as DrBoyMachineState);

function buildMachineState(
  entries: MachineSensorEntry[],
  fields: readonly DrBoyMachineOverviewField[],
): DrBoyMachineState {
  return fields.reduce((acc, field, index) => {
    const entry = entries[index];
    if (entry) {
      acc[field] = normalizeSensorEntry(entry);
    }
    return acc;
  }, createInitialMachineState());
}

export function hasDrBoyMachineData(
  state: DrBoyMachineState | undefined,
): boolean {
  return !!state && Object.values(state).some((v) => v.value != null);
}

export const fetchDrBoyMachineData = async (
  group: string,
  nodes: DrBoyMachineNodeMapping,
): Promise<DrBoyMachineDataResponse> => {
  const now = new Date();
  const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);

  const queryParams = {
    limit: 1,
    from: thirtyMinutesAgo.toISOString(),
    to: now.toISOString(),
    limit_order: "DESC" as const,
  };

  try {
    const sensorResults = await Promise.all(
      DR_BOY_MACHINE_OVERVIEW_FIELDS.map((field) =>
        getQueryTimeseriesForNodeUseCase({
          group,
          source: nodes[field],
          ...queryParams,
        }),
      ),
    );

    if (sensorResults.some((res) => !res.success)) {
      console.error(
        `One or more DR BOY queries failed for machine ${group}:`,
        sensorResults,
      );
      return {};
    }

    const entries = sensorResults.map((res) => res.data as MachineSensorEntry);

    return {
      [group]: buildMachineState(entries, DR_BOY_MACHINE_OVERVIEW_FIELDS),
    };
  } catch (err) {
    console.error(`Failed to fetch DR BOY data for machine ${group}`, err);
    return {};
  }
};
